/**
 * Pure mapper from the SPA's EPS-shaped referral request (produced by
 * `buildPayload`) to the shared lead request body used for LeadIntake XML.
 *
 * This is a lead-only MVP transform:
 * - `leadSource` / `originalLeadSource` are fixed to `TurboVets_VA`.
 * - `leadOwner` is HARDCODED to `posbanker`. There is deliberately no code path
 *   that lets form data, user input, runtime config, or URL parameters override
 *   it (see the mapper tests).
 * - `isCallable` is `false` and `supplemental.status` is `"U"`.
 * - No Empower `loanId`/`loanNumber` and no loan GUID are produced.
 * - The applicant maps to `lead.borrower` with `isPrimaryBorrower: true`; an
 *   opted-in co-borrower maps to `lead.coBorrower` with `isPrimaryBorrower:
 *   false`, and the property is absent entirely otherwise.
 */
import type { BuildPayloadResult } from "./payload-builder";
import {
  LEAD_OWNER_POSBANKER,
  LEAD_SOURCE_TURBOVETS,
  LEAD_SUPPLEMENTAL_STATUS_UNCALLABLE,
  LMD_ROUTING_INTENT,
  LMD_SOURCE_SYSTEM,
  mapCreditProfileToLmd,
  mapLoanPurposeToLmd,
  mapMilitaryBranchToLmd,
  mapPropertyTypeToLmd,
  type LmdApplication,
  type LmdBorrower,
  type LmdLead,
  type LmdUpsertLeadRequest,
} from "../models/lmd";
import type { ReferralBorrowerRequest } from "../models/referral";

type BuiltSuccess = Extract<BuildPayloadResult, { ok: true }>;

export interface LmdMapperOptions {
  /**
   * Optional lead-GUID source. By default NO client-generated GUID is sent:
   * LeadIntake returns the accepted LDGUID. Supplying a random GUID per submit
   * would defeat any server-side dedupe on retries. A factory is accepted only
   * so a future confirmed contract can opt in explicitly.
   */
  leadGuidFactory?: () => string;
}

/**
 * Maps the applicant. Its phone keeps the exact ten-digit string representation
 * this SPA has always sent; only the explicit primary role flag is added.
 */
function mapPrimaryBorrower(b: ReferralBorrowerRequest): LmdBorrower {
  const borrower: LmdBorrower = {};
  if (b.firstName) borrower.firstName = b.firstName;
  if (b.lastName) borrower.lastName = b.lastName;
  if (b.email) borrower.emailAddress = b.email;
  if (b.phone) borrower.phone1Number = b.phone;
  borrower.isPrimaryBorrower = true;
  return borrower;
}

/**
 * Maps the optional co-borrower. The contact fields are the same existing LMD
 * ones the applicant uses, with three differences the contract specifies for the
 * secondary contact: the phone preserves its ten-digit representation, the optional mailing address is
 * carried, and the role flag is false. Every field is emitted only when the
 * validated payload actually supplied it, so nothing is sent as null or blank.
 */
function mapCoBorrower(b: ReferralBorrowerRequest): LmdBorrower {
  const coBorrower: LmdBorrower = {};
  if (b.firstName) coBorrower.firstName = b.firstName;
  if (b.lastName) coBorrower.lastName = b.lastName;
  if (b.email) coBorrower.emailAddress = b.email;
  const digits = b.phone.replace(/\D/g, "");
  if (digits !== "") coBorrower.phone1Number = digits;
  if (b.streetAddress) coBorrower.mailStreetAddress = b.streetAddress;
  if (b.city) coBorrower.mailCity = b.city;
  if (b.state) coBorrower.mailState = b.state;
  if (b.zipCode) coBorrower.mailZipCode = b.zipCode;
  coBorrower.isPrimaryBorrower = false;
  return coBorrower;
}

/**
 * Builds the shared lead request envelope from a successful payload-builder
 * result. The lead owner and lead source are constants sourced only from this
 * module, never from `built`.
 */
export function buildLmdUpsertRequest(
  built: BuiltSuccess,
  options: LmdMapperOptions = {},
): LmdUpsertLeadRequest {
  const request = built.request;
  const refinanceType = built.purpose === "refinance" ? built.request.refinanceType : undefined;

  const application: LmdApplication = {
    loanPurpose: mapLoanPurposeToLmd(built.purpose, refinanceType),
    propertyType: mapPropertyTypeToLmd(request.propertyType),
    propertyState: request.propertyState,
    loanAmount: request.loanAmount,
    estimatedPropertyValue: request.estimatedValue,
  };

  if (built.purpose === "purchase") application.downPayment = built.request.downPayment;

  const creditProfile = mapCreditProfileToLmd(request.borrower.creditScoreRange);
  if (creditProfile) application.creditProfile = creditProfile;
  // The military question is application-level, so both wizard journeys map it
  // through this single path. Both fields are derived independently and each is
  // omitted rather than sent as null when the application does not assert it.
  // The legacy purchase form never answers the question explicitly (its control
  // is default-checked, asserting nothing about the applicant), so its wire
  // shape stays exactly as it is until WI-TV-W6-004 retires it.
  if (request.militaryService && (built.purpose === "refinance" || built.militaryAnswered)) {
    application.isMilitary = request.militaryService.isMilitary;
    const militaryBranch = mapMilitaryBranchToLmd(request.militaryService.branch);
    if (militaryBranch) application.militaryBranch = militaryBranch;
  }
  if (request.propertyStreetAddress) application.propertyStreetAddress = request.propertyStreetAddress;
  if (request.propertyCity) application.propertyCity = request.propertyCity;
  if (request.propertyZipCode) application.propertyZipCode = request.propertyZipCode;
  if (built.purpose === "refinance") {
    application.currentLoanBalance = built.request.currentLoanBalance;
  }

  const lead: LmdLead = {
    leadSource: LEAD_SOURCE_TURBOVETS,
    originalLeadSource: LEAD_SOURCE_TURBOVETS,
    leadOwner: LEAD_OWNER_POSBANKER,
    isCallable: false,
    supplemental: { status: LEAD_SUPPLEMENTAL_STATUS_UNCALLABLE },
    borrower: mapPrimaryBorrower(request.borrower),
    application,
  };

  // Present only for an application that actually has a co-borrower: the payload
  // builder omits the object for a declined, skipped, or removed one, so the
  // property is absent from the wire body rather than null or empty.
  if (request.coBorrower) lead.coBorrower = mapCoBorrower(request.coBorrower);

  const leadGuid = options.leadGuidFactory?.();
  if (leadGuid) lead.leadGuid = leadGuid;

  return {
    lead,
    sourceSystem: LMD_SOURCE_SYSTEM,
    routingIntent: LMD_ROUTING_INTENT,
  };
}
